'use client';

import { type BillingPeriod, PLANS } from '@/entities/plan';
import { LogoutButton } from '@/features/auth/logout-button';
import { SUBSCRIPTION_PATHNAME } from '@/shared/constants/pathnames';
import { Button, Separator, Text } from '@/shared/ui/core';
import { BillingToggle } from '@/shared/ui/kit/billing-toggle';
import { PlanCard } from '@/shared/ui/kit/plan-card';
import { RequiredDataNote } from '@/shared/ui/kit/required-data-note';
import { RegistrationCard, RegistrationCardContent, RegistrationCardDescription, RegistrationCardHeader, RegistrationCardTitle } from '@/shared/ui/layout/registration-card';
import { ArrowRightIcon, RepeatIcon } from '@phosphor-icons/react/ssr';
import { useRef } from 'react';
import { useSplitWizard } from '../../../model/split-wizard-context';
import { useSplitWizardSubmit } from '../../../model/split-wizard-submit-context';
import { WizardFooterSplit } from '../../wizard-footer-split';
import { BillingAddressSubForm, type BillingAddressSubFormHandle } from './billing-address-sub-form';
import styles from './step-payment.module.scss';


export const StepPaymentSplit = () => {
    const { state, dispatch } = useSplitWizard();

    const billingRef = useRef<BillingAddressSubFormHandle>(null);

    const { submit: signup, loading: signupLoading, error: signupError } = useSplitWizardSubmit();

    const selectedPlan  = state.data.plan
        ? PLANS.find(p => p.id === state.data.plan!.plan_id)
        : undefined;
    const billingPeriod = state.data.plan?.billing_period ?? 'monthly';

    const setBillingPeriod = (next: BillingPeriod) => {
        if (!state.data.plan) return;
        dispatch({
            type : 'commit',
            key  : 'plan',
            value: { ...state.data.plan, billing_period: next },
        });
    };

    const handleRegister = async () => {
        const billingData = await billingRef.current?.submit();
        if (!billingData) return;

        if (!state.data.plan) return;

        const result = await signup({
            ...state.data,
            billing_address: billingData,
        });

        if (!result) return;

        if (result.checkoutUrl) {
            window.location.href = result.checkoutUrl;
            return;
        }

        window.location.href = SUBSCRIPTION_PATHNAME;
    };

    const handleChangePlan = () => {
        dispatch({ type: 'goto', step: 'plan' });
    };

    return (
        <div>
            <RegistrationCard data-template="registration-steps-card">
                <RegistrationCardHeader data-template="registration-steps-card-header">
                    <RegistrationCardTitle data-template="registration-steps-card-title">
                        Billing information
                    </RegistrationCardTitle>

                    <RegistrationCardDescription data-template="registration-steps-card-description">
                        Please fill out the form to complete registration.
                    </RegistrationCardDescription>
                </RegistrationCardHeader>

                <RegistrationCardContent data-template="registration-steps-card-content">
                    <div data-template="step-payment" className={ styles.root }>
                        {
                            selectedPlan &&
                            <div className={ styles.plan_summary }>
                                <div className={ styles.header_slot }>
                                    <Text
                                        as="h3"
                                        weight="semibold"
                                        lineHeight={ 1.2 }
                                    >
                                        Selected plan
                                    </Text>

                                    <Text
                                        as="div"
                                        size="lg"
                                        tone="secondary"
                                        weight="regular"
                                        lineHeight={ 1.2 }
                                    >
                                        You can change it now or later.
                                    </Text>
                                </div>

                                <PlanCard
                                    plan={ selectedPlan }
                                    billingPeriod={ billingPeriod }
                                    isSelected={ false }
                                    isStandalone
                                    Button={
                                        <Button
                                            leftIcon={ <RepeatIcon/> }
                                            onClick={ handleChangePlan }
                                        >
                                            Change
                                        </Button>
                                    }
                                />

                                <Text
                                    as="h3"
                                    weight="semibold"
                                    lineHeight={ 1.2 }
                                    className={ styles.subheader }
                                >
                                    Billing period
                                </Text>

                                <BillingToggle
                                    value={ billingPeriod }
                                    onChange={ setBillingPeriod }
                                    discountPercent={ 0 }
                                />
                            </div>
                        }

                        {
                            !selectedPlan &&
                            <div className={ styles.plan_summary }/>
                        }

                        <Separator orientation="vertical" className={ styles.separator_left }/>

                        <div className={ styles.billing }>
                            <div className={ styles.header_slot }>
                                <Text
                                    as="h3"
                                    weight="semibold"
                                    lineHeight={ 1.2 }
                                >
                                    Billing information
                                </Text>

                                <Text
                                    as="div"
                                    size="lg"
                                    tone="secondary"
                                    weight="regular"
                                    lineHeight={ 1.2 }
                                >
                                    Enter your details for billing information.
                                </Text>
                            </div>

                            <BillingAddressSubForm
                                ref={ billingRef }
                                stateData={ state.data }
                                onSubmit={ (data) => dispatch({ type: 'commit', key: 'billing_address', value: data }) }
                            />

                            {
                                signupError &&
                                signupError.kind === 'generic' &&
                                <Text
                                    size="sm"
                                    tone="destructive"
                                    className={ styles.error }
                                >
                                    { signupError.message }
                                </Text>
                            }
                        </div>
                    </div>
                </RegistrationCardContent>
            </RegistrationCard>

            <WizardFooterSplit
                state={ state }
                left={
                    <div className={ styles.left }>
                        <RequiredDataNote className={ styles.required }/>
                        <LogoutButton variant='secondary'/>
                    </div>
                }
                right={
                    <Button
                        leftIcon={ <ArrowRightIcon/> }
                        onClick={ handleRegister }
                        loading={ signupLoading }
                    >
                        Submit
                    </Button>
                }
            />
        </div>
    );
};
